import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import MarketOrdersIndicator from 'games/PredictionMarket/components/Header/MarketOrdersIndicator'
import NotificationBell from 'games/PredictionMarket/components/Header/NotificationBell'
import HowItWorks from 'games/PredictionMarket/components/Header/HowItWorks'
import { userActivityReceived } from 'games/PredictionMarket/slice-thunk/predictionMarket.slice'
import { getCategories } from 'games/PredictionMarket/slice-thunk/predictionMarket.thunk'
import { selectPredictionAuth } from 'games/PredictionMarket/predictionMarket.auth'
import useUserSocketNameSpace from 'socket-resources/hooks/useUserSocketNameSpace'
import { getAuthToken } from 'helpers/cookie.helpers'
import { ROUTE_PATHS } from 'constants/index'

const PortfolioIcon = () => (
  <svg viewBox='0 0 24 24' width='16' height='16' aria-hidden='true'>
    <path
      fill='currentColor'
      d='M9 3h6a2 2 0 0 1 2 2v1h2.5A1.5 1.5 0 0 1 21 7.5v11A2.5 2.5 0 0 1 18.5 21h-13A2.5 2.5 0 0 1 3 18.5v-11A1.5 1.5 0 0 1 4.5 6H7V5a2 2 0 0 1 2-2zm0 3h6V5H9v1z'
    />
  </svg>
)

const HowItWorksIcon = () => (
  <svg viewBox='0 0 24 24' width='16' height='16' aria-hidden='true'>
    <path
      fill='currentColor'
      d='M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 4a1.25 1.25 0 1 1 0 2.5A1.25 1.25 0 0 1 12 6zm1.5 11.5h-3v-1.25h.75V12H10.5v-1.25h3v5.5h.75V17.5z'
    />
  </svg>
)

const isPredictionMarketPath = (pathname = '') =>
  pathname === ROUTE_PATHS.predictionMarket ||
  pathname.startsWith(`${ROUTE_PATHS.predictionMarket}/`)

const actionBtnClass =
  'inline-flex h-10 min-h-10 shrink-0 cursor-pointer appearance-none items-center justify-center gap-1.5 rounded-lg border-0 bg-[var(--gray-80)] px-3 text-sm font-semibold text-gold transition-[background,color,border-color] duration-150 hover:border-gold-dark hover:text-gold-bright [&_svg]:block [&_svg]:h-4 [&_svg]:w-4 [&_svg]:fill-current'

const iconBtnClass =
  'inline-flex h-10 w-10 min-h-10 min-w-10 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-lg border-0 bg-[var(--gray-80)] p-0 text-gold transition-[background,color,border-color] duration-150 hover:border-gold-dark hover:text-gold-bright [&_svg]:block [&_svg]:h-4 [&_svg]:w-4 [&_svg]:fill-current'

const PredictionMarketHeaderActions = ({ onToggleNotifications, onNavigate }) => {
  const { t } = useTranslation()
  const token = getAuthToken()
  const history = useHistory()
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const listenUserActivity = useUserSocketNameSpace(token)
  const { userInfo } = useSelector(selectPredictionAuth)
  const { isDemoMode, demoWallet } = useSelector((state) => state.predictionMarket)
  const isLoggedIn = Boolean(token)
  const onPredictionMarket = isPredictionMarketPath(pathname)
  const [howItWorksOpen, setHowItWorksOpen] = useState(false)

  useEffect(() => {
    if (!onPredictionMarket) return
    dispatch(getCategories())
  }, [onPredictionMarket, dispatch])

  useEffect(() => {
    if (!token || isDemoMode || !onPredictionMarket) return undefined

    const cleanUserActivity = listenUserActivity(({ data }) => {
      if (data) dispatch(userActivityReceived(data))
    })

    return () => {
      cleanUserActivity?.()
    }
  }, [token, isDemoMode, onPredictionMarket, dispatch])

  if (!onPredictionMarket) return null

  const handleExitDemo = () => {
    onNavigate?.()
    window.location.assign(ROUTE_PATHS.predictionMarketMarkets)
  }

  return (
    <div className='pm-header-actions relative z-20 mr-3 flex shrink-0 items-center gap-2'>
      {isDemoMode ? (
        <>
          <div
            className='inline-flex h-10 min-h-10 items-center gap-1.5 rounded-lg bg-[var(--gray-80)] px-3 text-sm font-semibold text-white'
            aria-label={t('demo')}
          >
            <span className='text-[var(--gray-20)]'>{demoWallet?.currency || 'USD'}</span>
            <span>{Number(demoWallet?.balance ?? 0).toFixed(2)}</span>
          </div>
          <button
            type='button'
            className={actionBtnClass}
            onClick={handleExitDemo}
            aria-label={t('exitTutorialAndReturnToMarkets')}
            title={t('exitDemo')}
          >
            {t('exitDemo')}
          </button>
        </>
      ) : (
        <button
          type='button'
          className={actionBtnClass}
          aria-label={t('howItWorks')}
          title={t('howItWorks')}
          onClick={() => {
            onNavigate?.()
            setHowItWorksOpen(true)
          }}
        >
          <HowItWorksIcon />
          <span className='max-md:hidden'>{t('howItWorks')}</span>
        </button>
      )}

      {isLoggedIn && !isDemoMode && (
        <>
          <MarketOrdersIndicator isLoggedIn={isLoggedIn} />
          <NotificationBell
            isLoggedIn={isLoggedIn}
            onToggle={onToggleNotifications}
          />
          <button
            type='button'
            className={iconBtnClass}
            aria-label='Portfolio'
            title='Portfolio'
            onClick={() => {
              onNavigate?.()
              history.push(`${ROUTE_PATHS.predictionMarketPortfolio}?userId=${userInfo?.user?.id || ''}`)
            }}
          >
            <PortfolioIcon />
          </button>
        </>
      )}

      {!isDemoMode && (
        <HowItWorks open={howItWorksOpen} onClose={() => setHowItWorksOpen(false)} />
      )}
    </div>
  )
}

export default PredictionMarketHeaderActions
